import {
  Channel,
  GenericProvider,
  Provider,
} from '@/features/game-context/types';
import { FgpLaunchData } from '@/features/providers/types';

export enum GameDataLoadingState {
  NotStarted = 'not-started',
  Loading = 'loading',
  Completed = 'completed',
  Failed = 'failed',
}

export type SeamlessLoginData = {
  player: {
    playerGuid: string;
  };
  token: string;
};

export type SessionContext = {
  session_code: string;
  fdSessionCode: string;
  fdBrand: string;
  fdProduct: string;
  fdState: string;
  fdVersion: string;
};

export interface SeamlessLoginPayload extends SessionContext {
  token: string;
  authToken: string;
}

export type SeamlessLoginResponse = {
  data: SeamlessLoginData;
  error: Record<string, unknown>;
  status: 'SUCCESS' | 'ERROR';
  reference: string;
};

export type DeviceInfo = {
  type: string;
  os: string;
  appType: string;
  ipAddress: string;
};

export interface FetchFgpGameDataPayload {
  loginToken: string;
  authToken: string;
  sessionCode: string;
  platform: string;
  userId: string;
  provider: string;
  providerRef: string;
  ip: string;
  gameUid: string;
  lobbyUrl: string;
  jackpotMode?: boolean;
  geolocationToken?: string;
  product?: string;
  slices?: Record<string, unknown>;
  channel?: Channel;
}

export interface FetchFgpGameDataInput
  extends Omit<FetchFgpGameDataPayload, 'jackpotMode'> {
  jackpotMode?: string;
  fdState: string;
  provider: Provider | GenericProvider;
}

export type FetchFgpGameResponse<T extends FgpLaunchData> = {
  data: T;
  error: Record<string, unknown>;
};

export type GameDetailsInput = {
  authToken: string;
  loginToken: string;
  brand: string;
  providerRef?: string;
  providerRefDesktop?: string;
  providerRefMobile?: string;
  provider: Provider | GenericProvider;
  region: string;
  ipAddress: string;
  userId: string;
  jackpotMode?: string;
  lobbyUrl?: string;
  gameUid?: string;
  geolocationToken?: string;
  product?: string;
  directLaunchParams?: Record<string, unknown>;
};

export type FormFactor = 'phone' | 'tablet' | 'desktop' | 'tv' | 'hybrid';

export type OS = 'WINDOWS' | 'MAC' | 'ANDROID' | 'IOS';

export const FORM_FACTOR = {
  phone: 'PHONE',
  tablet: 'TABLET',
  desktop: 'DESKTOP',
  tv: 'TV',
  hybrid: 'HYBRID',
} as const;

export type UserDevice =
  | (typeof FORM_FACTOR)[keyof typeof FORM_FACTOR]
  | 'UNKNOWN';

export type ProviderNameMap = {
  [provider in Provider | GenericProvider]: string;
};

export const ProviderNameToFgp: ProviderNameMap = {
  IGT: 'IGT',
  LNW: 'LnW',
  EVOLUTION: 'Evolution',
  EVERI: 'Everi',
  DGC: 'DGC',
  HIGH5: 'LnW',
  ARISTOCRAT: 'Aristocrat',
  SPINGAMING: 'SpinGaming',
  SLINGO: 'Slingo',
  EZUGI: 'Ezugi',
  NETENT: 'NetEnt',
  PLAYTECH: 'Playtech',
  PLAYTECH_POKER: 'Playtech_poker',
  PRAGMATIC: 'Pragmatic',
  AWAGER: 'Awager',
  WHITE_HAT: 'WHITE_HAT',
  FUSION: 'Fusion',
  GAMECODE: 'GAMECODE',
  FLUTTER_STUDIOS: 'FLUTTER_STUDIOS',
  AGS: 'AGS',
  GAMING_REALMS: 'GAMING_REALMS',
};

export const PokerProviderNamesToFgp = [ProviderNameToFgp.PLAYTECH_POKER];
